import './styles.css'

import { useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@chakra-ui/react'
import { LabelProps } from '@editor/interfaces/canvas'
import { useAppSelector } from '@editor/store/app'
import { useToolUtilSelector } from '@editor/store/selectors'
import { StylesProvider, Tooltip } from '@infraloop/common'
import { Html } from '@react-three/drei'
import { omit } from 'lodash'

const PI_4 = Math.PI / 4
const PI_3_4 = (3 * Math.PI) / 4

/**
 * Label for warnings, complete with Tooltip!
 */
const Label = (props: LabelProps) => {
  const {
    point,
    label,
    labelPrefix = '⚠️ ',
    message,
    offset = [0, 0],
    styles: styleOps,
    backgroundColor = 'yellow',
    color = 'black',
    opacity,
    padding = ['8px', '2px'],
    visible = true,

    isAdjustingColors,
    autoAdjustAwayFromAngle,

    onEnter,
    onLeave,
    onDown,
    onUp,
    onClick,
  } = props

  // Store
  const labelFontSize = useAppSelector((state) => state.toolUtil.core.labelFontSize)
  const currentLabelBackgroundColor = useToolUtilSelector((state) => state.currentLabelBackgroundColor)
  const currentLabelTextColor = useToolUtilSelector((state) => state.currentLabelTextColor)

  // States
  const [translateX, setTranslateX] = useState(() => `calc(-50% + ${offset[0]}px)`)
  const [translateY, setTranslateY] = useState(() => `calc(-50% + ${offset[1]}px)`)
  const [arrowClass, setArrowClass] = useState('')

  // Refs
  const containerRef = useRef<HTMLDivElement>(null)

  // Vars
  const isInteractive = !!(onEnter || onLeave || onDown || onUp || onClick)
  const canBeSeen =
    visible && (opacity === undefined || opacity > 0 || (typeof styleOps?.opacity === 'number' && styleOps.opacity > 0))

  /**
   * Combine styles with defaults.
   * Deprecated props (outlineColor, outlineWidth, fontSize, padding) are merged into styles for backward compatibility.
   */
  const styles = useMemo(
    () => ({
      ...{
        backgroundColor,
        color,
        padding,
        opacity,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        borderRadius: 4,
      },
      ...styleOps,
      // Handle real-time color adjustments
      ...(isAdjustingColors
        ? {
            color: currentLabelTextColor || color,
            backgroundColor: currentLabelBackgroundColor || backgroundColor,
          }
        : {}),
    }),
    [
      backgroundColor,
      color,
      padding,
      opacity,
      styleOps,
      isAdjustingColors,
      currentLabelBackgroundColor,
      currentLabelTextColor,
    ],
  )

  /**
   * Calculate padding for Box component based on styles.padding prop.
   */
  const boxPadding = useMemo(() => {
    let pad: {
      padding?: number | string
      paddingBlock?: number | string
      paddingInline?: number | string
      paddingTop?: number | string
      paddingRight?: number | string
      paddingBottom?: number | string
      paddingLeft?: number | string
    } = {
      padding: '2px 4px',
    }
    if (typeof styles.padding === 'number') {
      pad = { padding: styles.padding }
    } else if (Array.isArray(styles.padding) && styles.padding.length === 2) {
      pad = { paddingInline: styles.padding[0], paddingBlock: styles.padding[1] }
    } else if (Array.isArray(styles.padding) && styles.padding.length === 4) {
      pad = {
        paddingTop: styles.padding[0],
        paddingRight: styles.padding[1],
        paddingBottom: styles.padding[2],
        paddingLeft: styles.padding[3],
      }
    }
    return pad
  }, [styles.padding])

  /**
   * Update translate styles on offset change.
   */
  useEffect(() => {
    let x = '-50%'
    let y = '-50%'

    // padding for angle adjustment
    let px = 0
    let py = 0

    if (autoAdjustAwayFromAngle !== undefined) {
      if (autoAdjustAwayFromAngle >= -PI_4 && autoAdjustAwayFromAngle < PI_4) {
        // Right
        x = '-100%'
        px = -10
        setArrowClass('label-arrow-right')
      } else if (autoAdjustAwayFromAngle >= PI_4 && autoAdjustAwayFromAngle < PI_3_4) {
        // Top
        y = '-0%'
        py = 10
        setArrowClass('label-arrow-top')
      } else if (autoAdjustAwayFromAngle >= PI_3_4 || autoAdjustAwayFromAngle < -PI_3_4) {
        // Left
        x = '0%'
        px = 10
        setArrowClass('label-arrow-left')
      } else {
        // Bottom
        y = '-100%'
        py = -10
        setArrowClass('label-arrow-bottom')
      }
    } else {
      setArrowClass('')
    }

    setTranslateX(`calc(${x} + ${offset[0] + px}px)`)
    setTranslateY(`calc(${y} + ${offset[1] + py}px)`)
  }, [offset, autoAdjustAwayFromAngle])

  if (message !== undefined) {
    return (
      <group>
        <Html
          ref={containerRef}
          position={point}
          zIndexRange={[5, 10]}
          style={
            {
              transform: `translateX(${translateX}) translateY(${translateY})`,
              top: `${autoAdjustAwayFromAngle}px`,
              pointerEvents: isInteractive ? 'auto' : 'none',
              transition: 'transform 0.2s ease-out',
              '--label-background-color': styles.backgroundColor,
            } as React.CSSProperties
          }
          className={canBeSeen ? arrowClass : ''}
        >
          <StylesProvider>
            <Tooltip
              showArrow
              positioning={{ placement: 'top' }}
              label={message}
              contentProps={{ className: 'canvas-tooltip', p: '8px', fontWeight: 'normal' }}
            >
              <Box
                display={canBeSeen ? 'block' : 'none'}
                fontSize={`${80 * labelFontSize}%`}
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
                onMouseDown={onDown}
                onMouseUp={onUp}
                onClick={onClick}
                css={omit(styles, 'padding')}
                {...boxPadding}
              >
                {labelPrefix}
                {label}
              </Box>
            </Tooltip>
          </StylesProvider>
        </Html>
      </group>
    )
  }

  return (
    <Html ref={containerRef} position={point} zIndexRange={[5, 10]}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onMouseDown={onDown}
        onMouseUp={onUp}
        onClick={onClick}
        style={{
          display: canBeSeen ? 'block' : 'none',
          fontSize: `${80 * labelFontSize}%`,
          ...omit(styles, 'padding'),
          ...boxPadding,
        }}
      >
        {labelPrefix}
        {label}
      </div>
    </Html>
  )
}

export default Label
